import { requireById } from './dom'
import { showToast } from './admin-ui'

type Channel = 'phone' | 'email' | 'chat'

interface Message {
  from: 'guest' | 'ai'
  text: string
}

interface Draft {
  subject: string
  body: string
}

interface Convo {
  id: string
  channel: Channel
  name: string
  time: string
  preview: string
  badge: string
  needsYou?: boolean
  /** One-line context above the thread: call length, detected language, etc. */
  meta: string
  messages: Message[]
  /** AI hand-over summary, shown before a draft awaiting approval. */
  summary?: string
  draft?: Draft
  /** "What Toura did" — the receipts under the thread. */
  outcome: string[]
}

const CONVOS: Convo[] = [
  {
    id: 'festival',
    channel: 'email',
    name: 'Sapphire Coast Tourism',
    time: '9:02 am',
    preview: 'Whale Festival weekend — program listing & group rates',
    badge: 'Needs you',
    needsYou: true,
    meta: 'Email · flagged for review — commercial terms',
    messages: [
      {
        from: 'guest',
        text: 'Hi Seabreeze — we’re putting the Whale Festival program together for the October long weekend. Could we list your cruises, and do you offer a rate for groups of 20+?',
      },
    ],
    summary:
      'They want you in the festival program and are asking about group rates for 20 October. I’ve drafted a yes with your standard group pricing — check it before it goes.',
    draft: {
      subject: 'Re: Whale Festival weekend',
      body: 'We’d love to be in the program. For groups of 20+ we offer 15% off the Morning Whale Cruise with a reserved section of the top deck — happy to hold the 9 am departure for festival Saturday…',
    },
    outcome: [],
  },
  {
    id: 'sarah',
    channel: 'phone',
    name: 'Sarah Mitchell',
    time: '8:15 am',
    preview: 'Booked · 4 seats, tomorrow 9:00 am',
    badge: 'AI · booked',
    meta: 'Incoming call · 1 min 42 s · answered on the second ring',
    messages: [
      { from: 'guest', text: 'Hi — do you have anything tomorrow morning? We’re four. Two adults, two kids.' },
      {
        from: 'ai',
        text: 'We do — tomorrow’s 9 am Morning Whale Cruise has plenty of room. Three hours on the water, $95 for adults and $70 for the kids. Shall I book the four of you?',
      },
      { from: 'guest', text: 'Yes please. It’s Sarah Mitchell.' },
      {
        from: 'ai',
        text: 'All booked, Sarah! I’ve just texted your confirmation, the waiver link and where to park. See you at berth 4 at 8:45.',
      },
    ],
    outcome: [
      'Created booking #2847 — 4 seats, Tue 9:00 am',
      'Applied child rate: 2 × adult, 2 × child',
      'Sent confirmation, waiver + parking details by SMS',
      'Updated Viator & GetYourGuide: 18 → 14 seats',
    ],
  },
  {
    id: 'visitor',
    channel: 'chat',
    name: 'Website visitor',
    time: '7:52 am',
    preview: 'Is the 9 am cruise OK for a 3-year-old?',
    badge: 'AI · answered',
    meta: 'Website chat · answered in 8 s',
    messages: [
      { from: 'guest', text: 'Is the 9 am cruise OK for a 3-year-old?' },
      {
        from: 'ai',
        text: 'Absolutely — all ages welcome. Under-3s cruise free on your lap, and the 9 am run is the calmest for little ones. We carry toddler-size life jackets on board.',
      },
      { from: 'guest', text: 'Great, thanks!' },
    ],
    outcome: ['Answered in 8 seconds', 'Sent the young-families FAQ link'],
  },
  {
    id: 'chen',
    channel: 'email',
    name: 'Mark Chen',
    time: '7:15 am',
    preview: 'Moved · Saturday booking → Sunday',
    badge: 'AI · rebooked',
    meta: 'Email · booking #2831 amended',
    messages: [
      {
        from: 'guest',
        text: 'Hi, we’re booked on Saturday’s Coastal Discovery Tour but our plans changed — any chance of the same thing Sunday?',
      },
      {
        from: 'ai',
        text: 'No problem at all, Mark — you’re moved to Sunday’s 10 am Coastal Discovery Tour, same seats. Sunday runs a dollar cheaper in the shoulder rate, so $30 is on its way back to your card. Fresh waiver link below.',
      },
    ],
    outcome: [
      'Moved booking #2831: Sat 15 → Sun 16 Aug',
      'Refunded $30 fare difference to card',
      'Re-issued waiver + what-to-bring list',
      'Released 2 Saturday seats to all channels',
    ],
  },
  {
    id: 'tanaka',
    channel: 'email',
    name: 'Yuki Tanaka',
    time: '6:40 am',
    preview: '2 seats held · Thursday 9:00 am',
    badge: 'AI · held',
    meta: 'Email · detected Japanese, replied in Japanese',
    messages: [
      { from: 'guest', text: '木曜日の朝のクルーズは空いていますか？大人2名です。' },
      {
        from: 'ai',
        text: 'お問い合わせありがとうございます。木曜日9時のモーニング・ホエールクルーズは空席がございます。大人2名様のお席を24時間お取り置きしました。下のリンクからご確認ください。',
      },
    ],
    outcome: [
      'Held 2 seats — Thu 13 Aug, 9:00 am (24 h)',
      'Replied in Japanese, 3 minutes after the email arrived',
      'Follow-up scheduled if the hold isn’t confirmed by tonight',
    ],
  },
  {
    id: 'dave',
    channel: 'phone',
    name: 'Dave Rowley',
    time: 'Sun 9:38 pm',
    preview: 'Do you still go out if it’s raining?',
    badge: 'AI · after hours',
    meta: 'Incoming call · Sunday 9:38 pm · you were off the clock',
    messages: [
      { from: 'guest', text: 'G’day — quick one. Do you still go out if it’s raining tomorrow?' },
      {
        from: 'ai',
        text: 'We do — the whales don’t mind a drizzle, and there’s a covered deck plus ponchos on board. If the swell ever makes us cancel, you’ll get a text by 6:30 am with a free reschedule or refund, your pick.',
      },
      { from: 'guest', text: 'Beauty. Might see you tomorrow then.' },
    ],
    outcome: ['Answered at 9:38 pm — no missed call', 'Texted Dave the booking link for tomorrow'],
  },
]

const CHANNEL_ICONS: Record<Channel, string> = {
  phone:
    '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 2.5h2.4l1.2 3-1.6 1.2a9.5 9.5 0 0 0 4.3 4.3l1.2-1.6 3 1.2v2.4c0 .6-.5 1-1 1C7 14 2 9 2 3.5c0-.5.4-1 1-1Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>',
  email:
    '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><rect x="2" y="3.5" width="12" height="9" rx="1.5" stroke="currentColor" stroke-width="1.4"/><path d="m3 5 5 4 5-4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  chat:
    '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2.5 3.5h11v7h-6l-3 2.5v-2.5h-2v-7Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>',
}

const CHANNEL_NAMES: Record<Channel, string> = { phone: 'Phone', email: 'Email', chat: 'Web chat' }

/**
 * The unified inbox: every call, email and web chat in one list, with the
 * transcript and a "what Toura did" receipt beside it. One conversation
 * waits on the operator — approving its draft is the demo's moment.
 */
export function initInbox(): void {
  const list = requireById('ibxlist')
  const detail = requireById('ibxdetail')
  const filters = requireById('ibxfilters')

  let selectedId = 'sarah'
  let filter: 'all' | 'needs' = 'all'
  let festivalSent = false

  function renderList(): void {
    const visible = CONVOS.filter((c) => filter === 'all' || c.needsYou)
    list.replaceChildren(
      ...visible.map((c) => {
        const item = document.createElement('button')
        item.type = 'button'
        item.className = `ibx-item${c.id === selectedId ? ' on' : ''}`
        item.setAttribute('role', 'listitem')
        item.innerHTML =
          `<span class="ic ic-${c.channel}" title="${CHANNEL_NAMES[c.channel]}">${CHANNEL_ICONS[c.channel]}</span>` +
          `<span class="ibx-mid"><b>${c.name}</b><small>${c.preview}</small></span>` +
          `<span class="ibx-end"><time>${c.time}</time><i class="tag${c.needsYou ? ' hot' : ''}">${c.badge}</i></span>`
        item.addEventListener('click', () => {
          selectedId = c.id
          renderList()
          renderDetail()
        })
        return item
      }),
    )
    if (visible.length === 0) {
      const empty = document.createElement('div')
      empty.className = 'ibx-empty'
      empty.textContent = 'Nothing needs you. Enjoy the water. 🌊'
      list.append(empty)
    }
  }

  function renderDetail(): void {
    const convo = CONVOS.find((c) => c.id === selectedId)
    if (!convo) {
      detail.replaceChildren()
      return
    }

    const who = convo.channel === 'phone' ? 'Caller' : convo.channel === 'email' ? convo.name : 'Visitor'
    const thread = convo.messages
      .map(
        (m) =>
          `<div class="ib-msg ${m.from}"><label>${m.from === 'ai' ? 'Toura' : who}</label><p>${m.text}</p></div>`,
      )
      .join('')

    const summary = convo.summary
      ? `<div class="ib-summary"><label>Toura</label><p>${convo.summary}</p></div>`
      : ''

    const draft = convo.draft
      ? festivalSent
        ? `<div class="ib-draft sent"><div class="ibd-head">Sent ✓ · 9:41 am</div><b>${convo.draft.subject}</b><p>${convo.draft.body}</p></div>`
        : `<div class="ib-draft"><div class="ibd-head">Draft reply — waiting on you</div><b>${convo.draft.subject}</b><p>${convo.draft.body}</p><button type="button" class="ibd-approve" id="ibdapprove">Approve &amp; send</button></div>`
      : ''

    const outcome = convo.outcome.length
      ? `<div class="ib-outcome"><label>What Toura did</label><ul>${convo.outcome.map((o) => `<li>${o}</li>`).join('')}</ul></div>`
      : ''

    detail.innerHTML =
      `<div class="ibd-meta mono">${convo.meta}</div>` + thread + summary + draft + outcome

    const approve = document.getElementById('ibdapprove')
    approve?.addEventListener('click', () => {
      festivalSent = true
      const festival = CONVOS.find((c) => c.id === 'festival')
      if (festival) {
        festival.needsYou = false
        festival.badge = 'Sent ✓'
        festival.outcome = ['Sent your approved reply at 9:41 am', 'Filed under Partnerships', 'Reminder set: festival program closes 5 Sep']
      }
      requireById('needscount').textContent = '0'
      requireById('inboxbadge').style.display = 'none'
      renderList()
      renderDetail()
      showToast('Reply sent and filed — that was the only thing waiting on you.')
    })
  }

  filters.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>('.avf')
    if (!button) return
    filter = button.dataset['filter'] === 'needs' ? 'needs' : 'all'
    filters.querySelectorAll('.avf').forEach((b) => b.classList.remove('on'))
    button.classList.add('on')
    renderList()
  })

  renderList()
  renderDetail()
}
